import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Typography, Stack, TextField, Button, IconButton, Tooltip, InputAdornment, Alert,
    CircularProgress, Snackbar, Chip, Switch, FormControlLabel, LinearProgress, Dialog,
    DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useSelector } from 'react-redux';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SensorsRoundedIcon from '@mui/icons-material/SensorsRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import PhoneAndroidRoundedIcon from '@mui/icons-material/PhoneAndroidRounded';
import { PRIMARY, PRIMARY_LIGHT } from '../theme';
import { card, solidBtn, ghostBtn, field, th, td, Panel, EmptyState, SkeletonRows, LoadAlert } from './uiKit';
import { initialsFromName as initials, paletteColor as colorFor, parseApiDate, fmtClockTime, fmtDayMon, avatarTone } from '../utils/format';
import http, { apiErrorMessage } from '../Api/http';
import { useCrumbActionsSetter } from '../utils/crumbActions';
import {
    GetActiveLocationTrips, GetLocationTrackingConfig, SaveLocationTrackingConfig,
    GetDeviceInfos, GetDeviceInfo,
} from '../Api/Api';
import { selectUserTypeId, USER_TYPE } from '../redux/slices/authSlice';

const pickList = (data) => {
    if (Array.isArray(data)) return data;
    for (const k of ['items', 'trips', 'devices', 'deviceInfos', 'rows']) if (Array.isArray(data?.[k])) return data[k];
    return [];
};

const stamp = (v) => {
    const d = parseApiDate(v);
    if (!d) return v ? String(v) : '—';
    return `${fmtDayMon(d)} · ${fmtClockTime(d)}`;
};

// "5 min ago" style — keeps the live list scannable at a glance.
const ago = (v) => {
    const d = parseApiDate(v);
    if (!d) return '';
    const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins} min ago`;
    const h = Math.floor(mins / 60);
    return h < 24 ? `${h}h ${mins % 60}m ago` : `${Math.floor(h / 24)}d ago`;
};

const iconBtn = { border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', height: 42, width: 42, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } };

function Toast({ snack, onClose }) {
    return (
        <Snackbar open={Boolean(snack)} autoHideDuration={3800} onClose={onClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
            <Alert onClose={onClose} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
        </Snackbar>
    );
}

/* ───────────────────────── Live trips ───────────────────────── */

const normalizeLiveTrip = (it) => ({
    id: it.id ?? it.tripId,
    employeeCode: it.employeeCode ?? '',
    employeeName: it.employeeName ?? it.fullName ?? '',
    site: it.locationName ?? it.clientLocationName ?? '',
    client: it.clientCompany ?? '',
    startedAt: it.startedAt ?? it.startTime ?? it.startedOn ?? '',
    lastSeen: it.lastSeenAt ?? it.lastPointAt ?? it.lastUpdatedOn ?? it.lastActivityAt ?? it.lastSeen ?? it.updatedOn ?? '',
});

const REFRESH_MS = 30000;

export function LiveTripsTab() {
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState('');
    const [, setTick] = useState(0);

    const load = useCallback(async (silent = false) => {
        if (silent) setRefreshing(true); else setLoading(true);
        try {
            const { data: body } = await http.get(GetActiveLocationTrips);
            if (body?.error) throw new Error(body.message || 'Could not load live trips.');
            setTrips(pickList(body?.data).map(normalizeLiveTrip));
            setError('');
        } catch (err) {
            if (err?.response?.status === 404) { setTrips([]); setError(''); }
            else setError(apiErrorMessage(err, 'Could not load live trips.'));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh every 30s, but only while the browser tab is actually visible.
    useEffect(() => {
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') { load(true); setTick((t) => t + 1); }
        }, REFRESH_MS);
        const onVisible = () => { if (document.visibilityState === 'visible') load(true); };
        document.addEventListener('visibilitychange', onVisible);
        return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVisible); };
    }, [load]);

    // The refresh button lives at the right end of the breadcrumb row.
    const setCrumbActions = useCrumbActionsSetter();
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Tooltip arrow title="Refresh">
                <span><IconButton onClick={() => loadRef.current(true)} disabled={loading || refreshing} sx={iconBtn}>
                    {refreshing ? <CircularProgress size={17} /> : <RefreshRoundedIcon sx={{ fontSize: 19 }} />}
                </IconButton></span>
            </Tooltip>,
        );
        return () => setCrumbActions(null);
    }, [setCrumbActions, loading, refreshing]);

    return (
        <Box sx={{ p: 2 }}>

            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                {loading ? (
                    <SkeletonRows count={4} />
                ) : error ? (
                    <Box sx={{ p: 2 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={() => load()} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{error}</LoadAlert>
                    </Box>
                ) : trips.length === 0 ? (
                    <EmptyState icon={SensorsRoundedIcon} title="No trips in progress" hint="When someone starts travelling to a site, they'll show up here." />
                ) : (
                    <Box sx={{ p: 1.4, display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {trips.map((t, i) => (
                            <Box key={t.id ?? i} sx={{ border: '1px solid #EEF0F6', borderRadius: '9px', p: 1.4, display: 'flex', alignItems: 'center', gap: 1.4, flexWrap: 'wrap' }}>
                                <Box sx={{ width: 36, height: 36, borderRadius: '10px', flexShrink: 0, ...avatarTone(t.employeeName), display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 2px 8px -3px ${colorFor(t.employeeName)}` }}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#fff' }}>{initials(t.employeeName) || '—'}</Typography>
                                </Box>
                                <Box sx={{ minWidth: 150, flex: 1 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{t.employeeName || t.employeeCode || 'Unknown'}</Typography>
                                    <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{t.employeeCode}</Typography>
                                </Box>
                                <Stack direction="row" spacing={0.7} sx={{ alignItems: 'center', minWidth: 170, flex: 1 }}>
                                    <PlaceRoundedIcon sx={{ fontSize: 15, color: PRIMARY }} />
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#475569' }}>{t.site || 'No site'}{t.client ? ` · ${t.client}` : ''}</Typography>
                                </Stack>
                                <Box sx={{ minWidth: 130 }}>
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.5 }}>Started</Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>{stamp(t.startedAt)}</Typography>
                                </Box>
                                <Box sx={{ minWidth: 130 }}>
                                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase', letterSpacing: 0.5 }}>Last seen</Typography>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#334155' }}>{t.lastSeen ? `${ago(t.lastSeen)} (${fmtClockTime(t.lastSeen)})` : '—'}</Typography>
                                </Box>
                                <Chip label="Active" size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 800, bgcolor: '#DCFCE7', color: '#16A34A' }} />
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

/* ───────────────────────── Tracking settings ───────────────────────── */

const CONFIG_FIELDS = [
    { key: 'coordIntervalSeconds', label: 'Location update interval (seconds)', min: 5, max: 3600, hint: 'How often the app sends a position fix.' },
    { key: 'broadcastThrottleSeconds', label: 'Live broadcast throttle (seconds)', min: 1, max: 60, hint: 'How often live positions are pushed to management monitors.' },
    { key: 'archiveAfterMonths', label: 'Archive after (months)', min: 1, max: 120, hint: 'Raw points are pruned to a simplified route after this long.' },
    { key: 'abandonAfterMinutes', label: 'Auto-abandon idle trips after (minutes)', min: 5, max: 1440, hint: 'Idle trips are automatically marked abandoned.' },
];
const DEFAULT_CONFIG = { coordIntervalSeconds: 30, broadcastThrottleSeconds: 5, archiveAfterMonths: 6, geofenceStopEnabled: true, abandonAfterMinutes: 30 };

const validateConfig = (form) => {
    const errs = {};
    CONFIG_FIELDS.forEach((f) => {
        const raw = form[f.key];
        const n = Number(raw);
        if (raw === '' || raw === null || !Number.isInteger(n) || n < f.min || n > f.max) {
            errs[f.key] = `Enter a whole number from ${f.min} to ${f.max}.`;
        }
    });
    return errs;
};

export function TrackingSettingsTab() {
    const userTypeId = useSelector(selectUserTypeId);
    // MasterAdmin reads across entities but doesn't change an entity's configuration.
    const readOnly = userTypeId === USER_TYPE.MASTER_ADMIN;
    const [form, setForm] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [saving, setSaving] = useState(false);
    const [tried, setTried] = useState(false);
    const [snack, setSnack] = useState(null);
    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetLocationTrackingConfig);
            if (body?.error) throw new Error(body.message || 'Could not load tracking settings.');
            const c = body?.data || {};
            setForm({
                coordIntervalSeconds: c.coordIntervalSeconds ?? DEFAULT_CONFIG.coordIntervalSeconds,
                broadcastThrottleSeconds: c.broadcastThrottleSeconds ?? DEFAULT_CONFIG.broadcastThrottleSeconds,
                archiveAfterMonths: c.archiveAfterMonths ?? DEFAULT_CONFIG.archiveAfterMonths,
                geofenceStopEnabled: c.geofenceStopEnabled ?? DEFAULT_CONFIG.geofenceStopEnabled,
                abandonAfterMinutes: c.abandonAfterMinutes ?? DEFAULT_CONFIG.abandonAfterMinutes,
            });
            setLoadError('');
        } catch (err) {
            if (err?.response?.status === 404) setLoadError('');
            else setLoadError(apiErrorMessage(err, 'Could not load tracking settings.'));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const errs = validateConfig(form);

    const save = async () => {
        setTried(true);
        if (Object.keys(errs).length) { notify('Fix the highlighted settings first.', 'warning'); return; }
        setSaving(true);
        try {
            const payload = {
                coordIntervalSeconds: Number(form.coordIntervalSeconds),
                broadcastThrottleSeconds: Number(form.broadcastThrottleSeconds),
                archiveAfterMonths: Number(form.archiveAfterMonths),
                geofenceStopEnabled: Boolean(form.geofenceStopEnabled),
                abandonAfterMinutes: Number(form.abandonAfterMinutes),
            };
            const { data: body } = await http.put(SaveLocationTrackingConfig, payload);
            if (body?.error) throw new Error(body.message || 'Could not save tracking settings.');
            notify('Tracking settings saved.');
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save tracking settings.'), 'error');
        } finally { setSaving(false); }
    };

    // Reload lives at the right end of the breadcrumb row.
    const setCrumbActions = useCrumbActionsSetter();
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Tooltip arrow title="Reload">
                <span><IconButton onClick={() => loadRef.current()} disabled={loading} sx={iconBtn}><RefreshRoundedIcon sx={{ fontSize: 19 }} /></IconButton></span>
            </Tooltip>,
        );
        return () => setCrumbActions(null);
    }, [setCrumbActions, loading]);

    return (
        <Box sx={{ p: 2 }}>

            <Panel title="Location tracking" icon={TuneRoundedIcon}>
                {loading ? (
                    <SkeletonRows count={4} />
                ) : loadError ? (
                    <Box sx={{ p: 2 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{loadError}</LoadAlert>
                    </Box>
                ) : (
                    <Box sx={{ p: 2.2, maxWidth: 640 }}>
                        {readOnly && <Alert severity="info" sx={{ mb: 2, borderRadius: '9px' }}>Tracking settings are managed by each entity's admin. You can view them here but not change them.</Alert>}
                        <Stack spacing={2}>
                            {CONFIG_FIELDS.map((f) => (
                                <TextField key={f.key} type="number" size="small" fullWidth label={f.label} value={form[f.key]}
                                    disabled={readOnly || saving}
                                    onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
                                    error={tried && Boolean(errs[f.key])}
                                    helperText={tried && errs[f.key] ? errs[f.key] : `${f.hint} (${f.min}–${f.max})`}
                                    slotProps={{ htmlInput: { min: f.min, max: f.max, step: 1 } }} sx={field} />
                            ))}
                            <FormControlLabel
                                control={<Switch checked={Boolean(form.geofenceStopEnabled)} disabled={readOnly || saving}
                                    onChange={(e) => setForm((p) => ({ ...p, geofenceStopEnabled: e.target.checked }))}
                                    sx={{ '& .Mui-checked': { color: PRIMARY }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: PRIMARY } }} />}
                                label={<Box>
                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>Stop the trip automatically at the site</Typography>
                                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>Ends a trip as arrived once the person enters the site's geofence.</Typography>
                                </Box>} />
                        </Stack>
                        {!readOnly && (
                            <Stack direction="row" spacing={1} sx={{ mt: 2.4 }}>
                                <Button onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.6 }}>
                                    {saving ? 'Saving…' : 'Save Settings'}
                                </Button>
                                <Button onClick={load} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Discard changes</Button>
                            </Stack>
                        )}
                    </Box>
                )}
            </Panel>
            <Toast snack={snack} onClose={() => setSnack(null)} />
        </Box>
    );
}

/* ───────────────────────── Device health ───────────────────────── */

const normalizeDevice = (it) => ({
    code: it.employeeCode ?? '',
    name: it.employeeName ?? '',
    battery: it.batteryPercent === null || it.batteryPercent === undefined ? null : Number(it.batteryPercent),
    deviceName: it.deviceName ?? '',
    network: it.network ?? '',
    reportedOn: it.reportedOn ?? '',
});

function BatteryBar({ value }) {
    if (!Number.isFinite(value)) return <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>—</Typography>;
    const low = value < 20;
    const color = low ? '#E11D48' : value < 50 ? '#F59E0B' : '#16A34A';
    return (
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 120 }}>
            <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, value))}
                sx={{ flex: 1, height: 7, borderRadius: 4, bgcolor: '#EEF0F6', '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 4 } }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: low ? '#E11D48' : '#334155', minWidth: 36, textAlign: 'right' }}>{value}%</Typography>
        </Stack>
    );
}

export function DeviceHealthTab() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [detail, setDetail] = useState(null); // { row, loading, data, error }

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetDeviceInfos);
            if (body?.error) throw new Error(body.message || 'Could not load device health.');
            setRows(pickList(body?.data).map(normalizeDevice));
            setError('');
        } catch (err) {
            if (err?.response?.status === 404) { setRows([]); setError(''); }
            else setError(apiErrorMessage(err, 'Could not load device health.'));
        } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => [r.name, r.code, r.deviceName, r.network].some((v) => String(v).toLowerCase().includes(q)));
    }, [rows, search]);

    const lowCount = rows.filter((r) => Number.isFinite(r.battery) && r.battery < 20).length;

    const openDetail = async (row) => {
        setDetail({ row, loading: true, data: null, error: '' });
        try {
            const { data: body } = await http.get(GetDeviceInfo, { params: { employeeCode: row.code } });
            if (body?.error) throw new Error(body.message || 'Could not load the device.');
            setDetail({ row, loading: false, data: body?.data ? normalizeDevice(body.data) : row, error: '' });
        } catch (err) {
            setDetail({ row, loading: false, data: null, error: err?.response?.status === 404 ? 'This employee has not reported a device yet.' : apiErrorMessage(err, 'Could not load the device.') });
        }
    };

    const d = detail?.data;

    // The low-battery count and refresh live at the right end of the breadcrumb row.
    const setCrumbActions = useCrumbActionsSetter();
    const loadRef = useRef(load);
    loadRef.current = load;
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {lowCount > 0 && <Chip label={`${lowCount} low battery`} size="small" sx={{ height: 24, fontWeight: 800, bgcolor: '#FEF2F2', color: '#E11D48' }} />}
                <Tooltip arrow title="Refresh">
                    <span><IconButton onClick={() => loadRef.current()} disabled={loading} sx={iconBtn}><RefreshRoundedIcon sx={{ fontSize: 19 }} /></IconButton></span>
                </Tooltip>
            </Box>,
        );
        return () => setCrumbActions(null);
    }, [setCrumbActions, lowCount, loading]);

    return (
        <Box sx={{ p: 2 }}>

            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ p: 1.5, gap: 1, alignItems: 'center', borderBottom: '1px solid #EEF0F6' }}>
                    <TextField size="small" placeholder="Search name, code, device, network…" value={search} onChange={(e) => setSearch(e.target.value)}
                        sx={{ ...field, width: { xs: '100%', sm: 320 } }}
                        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment> } }} />
                    <Box sx={{ ml: 'auto', fontSize: 12, color: '#98A0AE' }}>{filtered.length} device{filtered.length === 1 ? '' : 's'}</Box>
                </Stack>

                {loading ? (
                    <SkeletonRows count={5} />
                ) : error ? (
                    <Box sx={{ p: 2 }}>
                        <LoadAlert sx={{ borderRadius: '9px' }} action={<Button size="small" onClick={load} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{error}</LoadAlert>
                    </Box>
                ) : filtered.length === 0 ? (
                    <EmptyState icon={PhoneAndroidRoundedIcon} title={rows.length ? 'No matching devices' : 'No devices reported yet'} hint={rows.length ? 'Try a different search.' : 'Devices appear once staff open the mobile app.'} />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                            <thead>
                                <tr>{['Employee', 'Battery', 'Device', 'Network', 'Reported'].map((h) => <Box component="th" key={h} sx={th}>{h}</Box>)}</tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => (
                                    <Box component="tr" key={r.code} onClick={() => openDetail(r)} sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#FAFAFE' } }}>
                                        <Box component="td" sx={td}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0F172A' }}>{r.name || r.code}</Typography>
                                            <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{r.code}</Typography>
                                        </Box>
                                        <Box component="td" sx={{ ...td, minWidth: 170 }}><BatteryBar value={r.battery} /></Box>
                                        <Box component="td" sx={{ ...td, color: '#334155' }}>{r.deviceName || '—'}</Box>
                                        <Box component="td" sx={{ ...td, color: '#334155' }}>{r.network || '—'}</Box>
                                        <Box component="td" sx={{ ...td, color: '#334155', whiteSpace: 'nowrap' }}>{r.reportedOn ? `${stamp(r.reportedOn)} (${ago(r.reportedOn)})` : '—'}</Box>
                                    </Box>
                                ))}
                            </tbody>
                        </Box>
                    </Box>
                )}
            </Box>

            <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} maxWidth="xs" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{detail?.row?.name || detail?.row?.code}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280' }}>{detail?.row?.code}</Typography>
                </DialogTitle>
                <DialogContent>
                    {detail?.loading ? <SkeletonRows count={3} height={34} /> : detail?.error ? (
                        <Alert severity="warning" sx={{ borderRadius: '9px' }}>{detail.error}</Alert>
                    ) : d && (
                        <Stack spacing={1.4} sx={{ pt: 0.5 }}>
                            <Box><Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase', mb: 0.5 }}>Battery</Typography><BatteryBar value={d.battery} /></Box>
                            {[['Device', d.deviceName], ['Network', d.network], ['Reported', d.reportedOn ? `${stamp(d.reportedOn)} (${ago(d.reportedOn)})` : '']].map(([k, v]) => (
                                <Box key={k}><Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase' }}>{k}</Typography><Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#334155' }}>{v || '—'}</Typography></Box>
                            ))}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDetail(null)} sx={{ ...ghostBtn, height: 40, px: 2 }}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
